import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, ArrowLeft, ShoppingCart } from "lucide-react";
import { useCustomer } from "../context/CustomerContext";
import { useCart } from "../context/CartContext";
import { getStoreContext, getStoreNamespacedKey, storePath, getSubdomain } from "../utils/storeUtils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const { subdomain: paramSubdomain } = useParams();
  const subdomain = paramSubdomain || getSubdomain();
  const [searchParams] = useSearchParams();

  // Get session ID from search params
  const sessionId = searchParams.get("session_id") || searchParams.get("payment_intent");

  const [status, setStatus] = useState("loading");
  const [transaction, setTransaction] = useState(null);
  const [failureReason, setFailureReason] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const { loginWithToken } = useCustomer();
  const { clearCart } = useCart();

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(0);
    } else {
      // No session ID at all — can't check payment
      setStatus("error");
    }
  }, [sessionId]);

  const pollPaymentStatus = async (currentPollCount) => {
    if (currentPollCount >= 15) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const rawData = response.data;

      // Safely handle if the backend returned the transaction object wrapped in an Array
      const data = Array.isArray(rawData) ? rawData[0] : (rawData?.data || rawData);

      if (data?.payment_status === "paid" || data?.payment_status === "succeeded") {
        setStatus("success");
        setTransaction(data.transaction || data);
        clearCart(); // Empty the shopping cart
      } else if (data?.payment_status === "failed" || data?.payment_status === "expired" || data?.status === "cancelled" || data?.session_status === "expired") {
        setFailureReason(data?.failure_reason || null);
        setStatus("failed");
      } else {
        // Still pending, poll again passing the count recursively avoiding stale closures
        setPollCount(currentPollCount + 1);
        setTimeout(() => pollPaymentStatus(currentPollCount + 1), 2000);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col animate-fade-in">
        {!subdomain && <Header />}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="card-elevated p-8 text-center max-w-md">
            <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Processing Payment...
            </h2>
            <p className="text-slate-600">Please wait while we confirm your payment.</p>
          </div>
        </div>
        {!subdomain && <Footer />}
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col animate-fade-in">
        {!subdomain && <Header />}
        <div className="flex-1 flex items-center justify-center p-4 py-12 lg:py-20">
          <div className="card-elevated p-8 text-center max-w-lg">
            <div className="w-20 h-20 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-lime-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Payment Successful!
            </h2>
            <p className="text-slate-600 mb-6">
              Thank you for your order. Your payment has been processed successfully.
            </p>

            {transaction && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 mb-6 text-left overflow-hidden">
                <Accordion type="single" collapsible defaultValue="order-summary" className="w-full">
                  <AccordionItem value="order-summary" className="border-b-0">
                    <AccordionTrigger className="px-6 py-4 hover:bg-slate-50 hover:no-underline transition-colors">
                      <div className="flex items-center space-x-2">
                        <ShoppingCart className="w-5 h-5 text-indigo-600" />
                        <h3 className="font-semibold text-slate-900 text-base">Order Summary</h3>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-0 pt-2">
                      <div className="space-y-4">
                        {/* Products List */}
                        <div className="space-y-4">
                          {transaction.items?.map((item, index) => (
                            <div key={index} className="flex gap-4">
                              <div className="w-16 h-16 rounded-md bg-slate-50 border border-slate-100 overflow-hidden flex-shrink-0">
                                {item.image_url ? (
                                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-slate-300">
                                    <ShoppingCart size={20} />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0 flex flex-col justify-center">
                                <p className="text-sm font-medium text-slate-900 line-clamp-2">{item.name}</p>
                                <p className="text-sm text-slate-500 mt-1">Qty: {item.quantity}</p>
                              </div>
                              <div className="text-right flex flex-col justify-center flex-shrink-0">
                                <span className="font-medium text-slate-900">
                                  ${((item.display_price || item.base_price || 0) * item.quantity).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Cost Breakdown */}
                        <div className="border-t border-slate-100 pt-4 space-y-2 text-sm">
                          <div className="flex justify-between text-slate-600">
                            <span>Subtotal</span>
                            <span className="font-medium text-slate-900">${transaction.subtotal?.toFixed(2) || transaction.base_total?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="flex justify-between text-slate-600">
                            <span className="flex flex-col">
                              <span>Shipping</span>
                              {transaction.shipping_method && <span className="text-xs text-slate-400">{transaction.shipping_method}</span>}
                            </span>
                            <span className="font-medium text-slate-900">${transaction.shipping_cost?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="flex justify-between text-slate-600">
                            <span>Tax</span>
                            <span className="font-medium text-slate-900">${transaction.tax?.toFixed(2) || '0.00'}</span>
                          </div>
                        </div>

                        <div className="border-t border-slate-100 flex justify-between items-center bg-slate-50 -mx-6 px-6 py-4 mt-4">
                          <span className="font-bold text-slate-900 text-base">Total</span>
                          <span className="font-bold text-indigo-600 text-xl">${transaction.total?.toFixed(2)}</span>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}

            {transaction ? (
              <Button
                onClick={() => {
                  const context = getStoreContext();
                  const tokenKey = getStoreNamespacedKey('auth_token', context);
                  const isLoggedIn = !!localStorage.getItem(tokenKey);

                  if (isLoggedIn) {
                    // Go directly to account
                    const accountPath = context === 'main'
                      ? `/customer/account?order=${transaction.order_id}`
                      : `/store/${context}/account?order=${transaction.order_id}`;
                    navigate(accountPath);
                  } else if (transaction.is_guest) {
                    // Go to verify
                    const verifyPath = context === 'main'
                      ? `/order/verify?uid=${transaction.customer_id}&ref=${transaction.order_id}`
                      : `/store/${context}/order/verify?uid=${transaction.customer_id}&ref=${transaction.order_id}`;
                    navigate(verifyPath);
                  } else {
                    // Registered but not logged in -> Go to login with redirect
                    const redirectPath = context === 'main'
                      ? `/customer/account?order=${transaction.order_id}`
                      : `/store/${context}/account?order=${transaction.order_id}`;
                    const loginPath = context === 'main'
                      ? `/customer/login?redirect=${encodeURIComponent(redirectPath)}`
                      : `/store/${context}/login?redirect=${encodeURIComponent(redirectPath)}`;
                    navigate(loginPath);
                  }
                }}
                className="bg-indigo-600 hover:bg-indigo-700 rounded-full font-medium shadow-md h-12 px-8"
              >
                View Order Details
              </Button>
            ) : (
              <Link to={storePath(subdomain, "/") || '/products'}>
                <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full font-medium shadow-md h-12 px-8">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Continue Shopping
                </Button>
              </Link>
            )}
          </div>
        </div>
        {!subdomain && <Footer />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col animate-fade-in">
      {!subdomain && <Header />}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="card-elevated p-8 text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {status === "timeout" ? "Payment Status Unknown" : "Payment Failed"}
          </h2>
          <p className="text-slate-600 mb-2">
            {status === "timeout"
              ? "We could not confirm your payment status. Please check your email for confirmation."
              : "Your payment could not be processed. Please try again."}
          </p>
          {failureReason && (
            <p className="text-sm text-red-500 bg-red-50 rounded-lg px-4 py-2 mb-6">
              Reason: {failureReason}
            </p>
          )}
          {!failureReason && <div className="mb-6" />}
          <Link to={storePath(subdomain, "/") || '/products'}>
            <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full h-12 px-8">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Store
            </Button>
          </Link>
        </div>
      </div>
      {!subdomain && <Footer />}
    </div>
  );
}
